import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Balance, CreateRecipient, CreatedRecipient, Exhibitor, Template, VoucherType } from '../dtos';
import { getAndClearFromLocalStorage } from '../store';

type State = {
    exhibitor: Exhibitor | null;
    voucherType: VoucherType | null;
    dueDate: string | null;
    template: Template | null;
    recipients: CreateRecipient[];
    distributedAmount: number;
    availableAmount: number;
    createdRecipients: CreatedRecipient[];
    selectVoucherType: (exhibitor: Exhibitor, voucherType: VoucherType, dueDate?: string) => void;
    refreshVoucherType: (exhibitors: Exhibitor[], balances: Balance[]) => void;
    selectTemplate: (template: Template | null) => void;
    addRecipient: (recipient: CreateRecipient) => void;
    editRecipient: (targetIndex: number, recipient: CreateRecipient) => void;
    removeRecipient: (index: number) => void;
    plusRecipientAmount: (index: number) => void;
    minusRecipientAmount: (index: number) => void;
    clearDistributionState: (refresh: boolean) => void;
    addCreatedRecipients: (recipients: CreatedRecipient[]) => void;
    resetRecipientsAmount: () => void;
};

type Props = {
    children: React.ReactNode;
};

const DistributionContext = createContext<State>({} as State);

const DistributionProvider: React.FC<Props> = ({ children }) => {
    const [exhibitor, setExhibitor] = useState<Exhibitor | null>(getAndClearFromLocalStorage({
        module: 'distribution',
        key: 'exhibitor',
        defaultValue: null
    }));
    const [voucherType, setVoucherType] = useState<VoucherType | null>(getAndClearFromLocalStorage({
        module: 'distribution',
        key: 'voucher_type',
        defaultValue: null
    }));
    const [dueDate, setDueDate] = useState<string | null>(null);
    const [template, setTemplate] = useState<Template | null>(getAndClearFromLocalStorage({
        module: 'distribution',
        key: 'template',
        defaultValue: null
    }));
    const [recipients, setRecipients] = useState<CreateRecipient[]>(getAndClearFromLocalStorage({
        module: 'distribution',
        key: 'recipients',
        defaultValue: []
    }));
    const [createdRecipients, setCreatedRecipients] = useState<CreatedRecipient[]>([]);

    const distributedAmount = useMemo(() => recipients.reduce<number>((amount, recipient) =>
        amount + recipient.amount, 0), [recipients]);

    const availableAmount = useMemo(() => {
        if (!voucherType)
            return 0;

        return voucherType.amount - distributedAmount;
    }, [voucherType, distributedAmount]);

    const addRecipient = useCallback((recipient: CreateRecipient) =>
        setRecipients(oldRecipients => [...oldRecipients, recipient]), []);

    const editRecipient = useCallback((targetIndex: number, recipient: CreateRecipient) =>
        setRecipients(oldRecipients =>
            [...oldRecipients.map((oldRecipient, index) => index === targetIndex ? recipient : oldRecipient)]), []);

    const removeRecipient = useCallback((index: number) =>
        setRecipients(oldRecipients => {
            const newRecipients = [...oldRecipients];
            newRecipients.splice(index, 1);

            return newRecipients;
        }), []);

    const plusRecipientAmount = useCallback((index: number) => {
        if (availableAmount > 0)
            setRecipients(oldRecipients => oldRecipients.map((oldRecipient, currIndex) =>
                currIndex === index ? {
                    ...oldRecipient,
                    amount: oldRecipient.amount + 1
                } : oldRecipient)
            );
    }, [availableAmount]);

    const minusRecipientAmount = useCallback((index: number) => {
        setRecipients(oldRecipients => oldRecipients.map((oldRecipient, currIndex) => {
            if (currIndex === index)
                return oldRecipient.amount === 1 ? oldRecipient : ({
                    ...oldRecipient,
                    amount: oldRecipient.amount - 1
                });

            return oldRecipient;
        }));
    }, []);

    const selectVoucherType = useCallback((exhibitor: Exhibitor, voucherType: VoucherType, dueDate?: string) => {
        setExhibitor(exhibitor);
        setVoucherType(voucherType);
        setDueDate(dueDate || null);
    }, []);

    const refreshVoucherType = useCallback((exhibitors: Exhibitor[], balances: Balance[]) => {
        if (!exhibitor || !voucherType)
            return;

        let findedExhibitor: Exhibitor | undefined;
        let findedType: VoucherType | undefined;

        if (dueDate) {
            findedExhibitor = balances.find(balance => balance.dueDate === dueDate)?.exhibitors.find(({ id }) => id === exhibitor.id);
            findedType = findedExhibitor?.voucherTypes.find(({ id }) => id === voucherType.id);
        } else {
            findedExhibitor = exhibitors.find(({ id }) => id === exhibitor.id);
            findedType = findedExhibitor?.voucherTypes.find(({ id }) => id === voucherType.id);
        }

        setExhibitor(findedExhibitor || exhibitor);
        setVoucherType(findedType || ({
            ...voucherType,
            amount: 0
        }));
    }, [exhibitor, voucherType, dueDate]);

    const selectTemplate = useCallback((template: Template | null) => setTemplate(template), []);

    const clearDistributionState = useCallback((refresh: boolean) => {
        if (refresh && exhibitor && voucherType) {
            setVoucherType({
                ...voucherType,
                amount: voucherType.amount - distributedAmount,
            });
        } else {
            setExhibitor(null);
            setVoucherType(null);
        }

        setTemplate(null);
        setRecipients([]);
    }, [exhibitor, voucherType, distributedAmount]);

    const addCreatedRecipients = useCallback((recipients: CreatedRecipient[]) =>
        setCreatedRecipients(recipients), []);

    const resetRecipientsAmount = useCallback(() => setRecipients(oldRecipients => oldRecipients.map(
        recipient => ({
            ...recipient,
            amount: 1
        })
    )), []);

    return (
        <DistributionContext.Provider
            value={{
                exhibitor,
                voucherType,
                dueDate,
                template,
                recipients,
                distributedAmount,
                availableAmount,
                createdRecipients,
                selectVoucherType,
                refreshVoucherType,
                selectTemplate,
                addRecipient,
                editRecipient,
                removeRecipient,
                plusRecipientAmount,
                minusRecipientAmount,
                clearDistributionState,
                addCreatedRecipients,
                resetRecipientsAmount
            }}
        >
            {children}
        </DistributionContext.Provider>
    );
};

const useDistribution = (): State => {
    const context = useContext(DistributionContext);

    return context;
}

export { DistributionProvider, useDistribution };